import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff, ArrowLeft, Building2, ChevronDown, Check, Loader2, Phone, KeyRound } from 'lucide-react';

import { FormInput } from '../components/FormInput';

const OrgTypeBadge = ({ type }) => {
  let label;
  if (type === 'both') {
    label = 'Product & Service';
  } else if (type === 'service') {
    label = 'Service';
  } else if (type === 'product') {
    label = 'Product';
  } else {
    label = 'Product & Service';
  }

  return (
    <span className="text-[11px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider">
      {label}
    </span>
  );
};

OrgTypeBadge.propTypes = {
  type: PropTypes.string,
};

const OrgPicker = ({ orgs, selectedId, onSelect }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const selected = orgs.find((o) => o.id === selectedId);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };

    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };

    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onClick);

    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative min-w-0">
      <label
        htmlFor="org-picker"
        className="text-[11px] font-semibold uppercase tracking-[0.1em] text-gray-700 px-1"
      >
        Organization
      </label>

      <button
        id="org-picker"
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="mt-1 w-full flex items-center gap-3 bg-gray-50 border border-gray-100 hover:border-[var(--theme-primary-tint)] focus:border-[var(--theme-primary)] rounded-lg px-3 py-2.5 outline-none transition-all text-left min-w-0"
      >
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center text-white shadow-sm shrink-0"
          style={{
            background: selected ? selected.theme.primary : '#94a3b8',
          }}
          aria-hidden="true"
        >
          <Building2 size={16} />
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-xs sm:text-sm font-bold text-[#0d0d0d] truncate">
            {selected ? selected.name : 'Select an organization'}
          </p>

          {selected ? (
            <div className="flex items-center gap-2 mt-0.5">
              <OrgTypeBadge type={selected.type} />
            </div>
          ) : (
            <p className="text-[11px] sm:text-xs font-medium text-slate-400">
              Pick yours to load its theme
            </p>
          )}
        </div>

        <ChevronDown
          size={14}
          className={`text-slate-400 transition-transform shrink-0 ${open ? 'rotate-180' : ''
            }`}
          aria-hidden="true"
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.ul
            role="listbox"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 right-0 top-full mt-2 bg-white border border-[var(--theme-tertiary)] rounded-md shadow-xl overflow-hidden z-30 max-h-[180px] overflow-y-auto custom-scrollbar"
          >
            {orgs.map((org) => {
              const isSelected = org.id === selectedId;

              return (
                <li key={org.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => {
                      onSelect(org.id);
                      setOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${isSelected
                      ? 'bg-[var(--theme-primary-soft)]'
                      : 'hover:bg-slate-50'
                      }`}
                  >
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-white shrink-0"
                      style={{ background: org.theme.primary }}
                      aria-hidden="true"
                    >
                      <Building2 size={14} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="text-xs sm:text-sm font-bold text-[#0d0d0d] truncate">
                        {org.name}
                      </p>

                      <div className="flex items-center gap-2 mt-0.5">
                        <OrgTypeBadge type={org.type} />
                      </div>
                    </div>

                    {isSelected && (
                      <Check
                        size={14}
                        className="text-[var(--theme-primary)] shrink-0"
                        aria-hidden="true"
                      />
                    )}
                  </button>
                </li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};

OrgPicker.propTypes = {
  orgs: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
};

export const OrgLogin = ({
  setView,
  onLogin,
  showNotification,
  isLoaded,
  applyTheme,
  hideBack = false,
}) => {
  const [activeOrgs, setActiveOrgs] = useState([]);
  const [orgId, setOrgId] = useState('');
  // Whatever the shop asks for: an e-mail address, a mobile number, or either.
  // One value, because the shop has already decided which it is and the form
  // draws one box - a second field would be a question nobody needs answering.
  const [identity, setIdentity] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // Which of the three screens is up: signing in, typing the code that was
  // sent, or choosing a password. One value rather than a pair of booleans,
  // because the three are exclusive and a pair can be both at once.
  const [step, setStep] = useState('signin');
  const [code, setCode] = useState('');
  // What the server said about the sending, word for word.
  //
  // This screen wrote its own sentence - "We sent a 6-digit code to 6381629718"
  // - which it had no way of knowing. On a server with no SMS gateway nothing
  // went anywhere and nothing could, and the next screen then said the code was
  // wrong or expired, sending somebody looking for a message that was never
  // sent. Only the server knows what it can deliver, so it writes that line.
  const [sentNote, setSentNote] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  // One helper for the three code endpoints, because they differ only in the
  // path and the body and sharing the error handling keeps the three screens
  // saying the same thing when the server is unreachable.
  const callAuth = async (path, body) => {
    try {
      setLoading(true);
      const response = await fetch('/api/auth/' + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));
      return { ok: response.ok, data };
    } catch {
      if (showNotification) showNotification('Failed to connect to server');
      return { ok: false, data: {} };
    } finally {
      setLoading(false);
    }
  };

  const sendCode = async (e) => {
    if (e) e.preventDefault();
    if (!identity.trim()) {
      if (showNotification) showNotification(`Enter your ${idLabel.toLowerCase()}`);
      return;
    }
    const { ok, data } = await callAuth('send-code', { identity, orgId });
    if (!ok) {
      if (showNotification) showNotification(data.error || 'Could not send the code');
      return;
    }
    setSentNote(data.message || '');
    setCode('');
    setStep('code');
    if (data.delivery && showNotification) {
      showNotification('Verification code sent successfully', 'success');
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    const { ok, data } = await callAuth('verify-code', { identity, code });
    if (!ok) {
      if (showNotification) showNotification(data.error || 'That code is wrong');
      return;
    }
    setNewPassword('');
    setConfirmPassword('');
    setStep('newpass');
  };

  const savePassword = async (e) => {
    e.preventDefault();
    // Checked here so somebody is told kindly, and checked again on the server
    // so it cannot be skipped.
    if (newPassword !== confirmPassword) {
      if (showNotification) showNotification('The two passwords do not match.');
      return;
    }
    const { ok, data } = await callAuth('set-password', {
      identity, code, password: newPassword, confirm: confirmPassword,
    });
    if (!ok) {
      if (showNotification) showNotification(data.error || 'Could not set the password');
      return;
    }
    // Straight back to signing in, with the box they typed still filled, so the
    // only thing left to do is type the password they have just chosen.
    setPassword('');
    setCode('');
    setStep('signin');
    if (showNotification) showNotification('Password set successfully. Sign in with it.', 'success');
  };

  useEffect(() => {
    const fetchOrgs = async () => {
      try {
        const response = await fetch('/api/organizations');
        if (response.ok) {
          const data = await response.json();
          const mapped = data.map((org) => {
            let modulesObj = {};
            try {
              if (typeof org.modules === 'string') {
                modulesObj = JSON.parse(org.modules);
              } else if (org.modules && typeof org.modules === 'object') {
                modulesObj = org.modules;
              }
            } catch (e) {
              console.error("Failed to parse modules:", e);
            }
            return {
              ...org,
              theme: {
                primary: org.themePrimary || '#0a5132',
                secondary: org.themeSecondary || '#faf6ec',
                tertiary: org.themeTertiary || '#efe9d8',
              },
              modules: modulesObj,
            };
          });
          const active = mapped.filter(o => o.status === 'Active');
          setActiveOrgs(active);
          if (active.length > 0) {
            setOrgId(active[0].id);
          }
        }
      } catch (err) {
        console.error("Failed to fetch organizations:", err);
      }
    };
    fetchOrgs();
  }, []);

  const selectedOrg = useMemo(
    () => activeOrgs.find((o) => o.id === orgId) || null,
    [orgId, activeOrgs]
  );

  useEffect(() => {
    if (selectedOrg && applyTheme) {
      applyTheme(selectedOrg.theme);
    }
  }, [orgId, selectedOrg, applyTheme]);

  /**
   * What this shop signs in with, and therefore what the one box says.
   *
   * The shop is chosen above the box, so by the time this is drawn the answer
   * is known and the form can ask one question instead of offering a choice
   * nobody at a counter should have to understand.
   *
   * Email for anything unrecognised, including the empty column every shop
   * carried before this setting existed. A console that guessed otherwise would
   * show a shop a box it has never used.
   */
  const method = useMemo(() => {
    const m = String(selectedOrg?.loginMethod || '').trim().toLowerCase();
    return m === 'phone' || m === 'both' ? m : 'email';
  }, [selectedOrg]);

  const idLabel = method === 'phone' ? 'Mobile number'
    : method === 'both' ? 'Email or mobile number' : 'Email';
  const idPlaceholder = method === 'phone' ? '10-digit number'
    : method === 'both' ? 'you@example.com or 9876543210' : 'you@example.com';
  // `text` on the shared box, not `email` or `tel`. A browser asked for an
  // e-mail refuses a phone number before the form is ever submitted, and a
  // shop set to both would have its own staff told their number is invalid.
  const idType = method === 'phone' ? 'tel' : method === 'both' ? 'text' : 'email';
  const IdIcon = method === 'phone' ? Phone : Mail;
  const idAutoComplete = method === 'phone' ? 'tel' : method === 'both' ? 'username' : 'email';

  // A shop that signs in by number gets a box that only holds a number.
  //
  // Ten digits, nothing else, with a pasted +91 or a leading zero taken off -
  // the same rule the till applies and the same one the server stores by. A
  // number arrives pasted out of a message or a contact card as often as it is
  // typed, and "+91 98765 43210" cut blindly to ten characters is +9198765, a
  // number belonging to nobody.
  //
  // Only for a phone shop. A shop on "both" has one box that may hold an
  // address, and an address cannot be cleaned as a number without destroying it.
  const cleanIdentity = (v) => {
    if (method !== 'phone') return v;
    const d = v.replace(/\D/g, '');
    if (d.length === 12 && d.startsWith('91')) return d.slice(2);
    if (d.length === 11 && d.startsWith('0')) return d.slice(1);
    return d.slice(0, 10);
  };
  const PRIMARY = selectedOrg?.theme.primary || '#0a5132';

  // Typing an address and then picking a shop that wants a number leaves the
  // wrong thing in the box under the right label.
  useEffect(() => { setIdentity(''); }, [method]);

  const submit = async (e) => {
    e.preventDefault();

    if (!selectedOrg) {
      if (showNotification) {
        showNotification('Please select an organization');
      }
      return;
    }

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ identity, password, orgId: selectedOrg.id }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (showNotification) showNotification(data.error || 'Invalid credentials');
        return;
      }

      const u = data.user;
      
      if (u.orgId !== selectedOrg.id) {
        if (showNotification) showNotification('User does not belong to this organization');
        return;
      }

      if (u && (u.role === 'org-admin' || u.role === 'branch-admin' || u.role === 'warehouse-admin' || u.role === 'warehouse-user' || u.role === 'org-user')) {
        setIsExiting(true);
        setTimeout(() => {
          onLogin({ ...u, accessToken: data.accessToken, refreshToken: data.refreshToken }, selectedOrg);
        }, 400);
      } else {
        if (showNotification) showNotification('Invalid role for this organization');
      }
    } catch {
      if (showNotification) showNotification('Failed to connect to server');
    }
  };

  return (
    <div
      className={`relative w-full min-h-screen overflow-hidden flex flex-col overflow-x-hidden transition-all duration-500 ${isLoaded ? 'opacity-100' : 'opacity-0'} ${isExiting ? 'bg-transparent' : ''}`}
      style={{
        background: isExiting ? 'transparent' : (selectedOrg?.theme.secondary || '#faf6ec'),
      }}
    >
      <div
        aria-hidden="true"
        className={`absolute inset-0 pointer-events-none overflow-hidden transition-opacity duration-500 ${isExiting ? 'opacity-0' : 'opacity-100'}`}
      >
        <div
          className="absolute -top-32 -right-24 w-[260px] h-[260px] sm:w-[340px] sm:h-[340px] xl:w-[440px] xl:h-[440px] rounded-full opacity-60"
          style={{
            background: `radial-gradient(closest-side, ${selectedOrg?.theme.primary || '#0a5132'
              }30, transparent)`,
          }}
        />

        <div
          className="absolute -bottom-24 -left-24 w-[240px] h-[240px] sm:w-[320px] sm:h-[320px] xl:w-[420px] xl:h-[420px] rounded-full opacity-60"
          style={{
            background: `radial-gradient(closest-side, ${selectedOrg?.theme.tertiary || '#efe9d8'
              }, transparent)`,
          }}
        />
      </div>

      <div className="relative z-10 w-full flex-1 flex flex-col lg:flex-row min-h-screen pointer-events-none">
        <motion.aside
          key={selectedOrg?.id || 'default'}
          initial={{ opacity: 0, x: -24 }}
          animate={{ opacity: 1, x: isExiting ? '-100%' : 0 }}
          transition={{ duration: isExiting ? 0.4 : 0.45, ease: isExiting ? [0.76, 0, 0.24, 1] : undefined }}
          className="hidden lg:flex lg:w-1/2 flex-col text-white h-screen overflow-hidden transition-colors duration-700 pointer-events-auto"
          style={{
            background: selectedOrg
              ? `linear-gradient(
                  135deg,
                  ${selectedOrg.theme.primary} 0%,
                  color-mix(in srgb, ${selectedOrg.theme.primary} 70%, #000 30%) 100%
                )`
              : 'linear-gradient(135deg, #0a5132 0%, #063820 100%)',
          }}
        >


          <div className="h-[80%] w-full flex items-center justify-center overflow-hidden">
            <img
              src="/loginimg.png"
              alt="Mitra POS"
              className="w-full h-full drop-shadow-2xl select-none pointer-events-none"
            />
          </div>

          <div className="h-[20%] w-full flex flex-col justify-end px-3 pb-3 z-10">
            <h1 className="text-4xl xl:text-5xl font-extrabold leading-tight tracking-tight break-words uppercase">
              {selectedOrg
                ? selectedOrg.name
                : 'Pick your organization'}
            </h1>

            <p className="text-base text-white/70 max-w-lg">
              The app you're about to enter is themed to your organization.
              Colors, accents, and chrome adapt automatically once you sign in.
            </p>
            
            <div>
              <p className="text-[10px] font-medium tracking-wide text-white/50">
                &copy; {new Date().getFullYear()} Mitra POS. <span className="text-white/30">All rights reserved.</span>
              </p>
            </div>
          </div>
        </motion.aside>

        <motion.main 
          initial={{ opacity: 0, x: 32 }}
          animate={{ opacity: isExiting ? 0 : 1, x: isExiting ? '100%' : 0 }}
          transition={{ duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
          className="flex-1 flex flex-col w-full min-w-0 p-4 sm:p-6 md:p-8 lg:p-12 pointer-events-auto overflow-y-auto"
          style={{ background: selectedOrg?.theme.secondary || '#faf6ec' }}
        >
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="w-full max-w-[440px] min-w-0 bg-white border border-[var(--theme-tertiary)] rounded-3xl px-6 py-5 sm:px-8 sm:py-6 shadow-[0_30px_60px_-30px_rgba(15,23,42,0.25)] m-auto shrink-0"
          >
            {!hideBack && (
              <button
                type="button"
                onClick={() => setView('splash')}
                className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 hover:text-[var(--theme-primary)] transition-colors mb-4"
              >
                <ArrowLeft size={12} aria-hidden="true" />
                Back
              </button>
            )}

            <div className="flex items-center justify-center gap-3 mb-2">
              <img
                src={selectedOrg?.logo || "/logo.png"}
                alt={`${selectedOrg?.name || 'Mitra POS'} Logo`}
                className="w-12 h-12 object-contain shrink-0 drop-shadow-md"
              />
              <p className="text-[16px] font-extrabold uppercase tracking-[0.2em] text-[#0d0d0d]">
                {selectedOrg?.name || "MITRA POS"}
              </p>
            </div>

            {step === 'code' ? (
              <>
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#0d0d0d] text-center mt-1">
                  Enter the code
                </h2>
                <p className="mt-1.5 text-sm text-slate-500 text-center mb-6">
                  {sentNote || `We sent a 6-digit code to ${identity}.`}
                </p>

                <form className="mt-4 space-y-3" onSubmit={verifyCode}>
                  <FormInput
                    label="Code"
                    icon={KeyRound}
                    type="text"
                    inputMode="numeric"
                    placeholder="123456"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
                    autoComplete="one-time-code"
                    required
                  />

                  <button
                    type="submit"
                    disabled={loading || code.length < 6}
                    className="w-full inline-flex items-center justify-center gap-3 px-5 py-3.5 rounded-xl text-white font-bold text-sm transition-colors shadow-lg outline-none focus-visible:ring-4 min-w-0 hover:brightness-95 disabled:opacity-70"
                    style={{ background: PRIMARY, boxShadow: `0 14px 30px -10px ${PRIMARY}55` }}
                  >
                    {loading && <Loader2 size={16} className="animate-spin shrink-0" aria-hidden="true" />}
                    <span>{loading ? 'Checking...' : 'Verify'}</span>
                  </button>

                  <div className="flex items-center justify-between mt-4">
                    <button
                      type="button"
                      onClick={() => setStep('signin')}
                      className="text-sm font-bold transition-colors hover:underline"
                      style={{ color: PRIMARY }}
                    >
                      Back to Sign In
                    </button>
                    <button
                      type="button"
                      onClick={() => sendCode()}
                      disabled={loading}
                      className="text-sm font-bold transition-colors hover:underline disabled:opacity-60"
                      style={{ color: PRIMARY }}
                    >
                      Send it again
                    </button>
                  </div>
                </form>
              </>
            ) : step === 'newpass' ? (
              <>
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#0d0d0d] text-center mt-1">
                  Set your password
                </h2>
                <p className="mt-1.5 text-sm text-slate-500 text-center mb-6">
                  At least 6 characters. You will sign in with it from now on.
                </p>

                <form className="mt-4 space-y-3" onSubmit={savePassword}>
                  <FormInput
                    label="New password"
                    icon={Lock}
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    rightIcon={showPassword ? EyeOff : Eye}
                    onRightIconClick={() => setShowPassword((v) => !v)}
                    autoComplete="new-password"
                    required
                  />
                  <FormInput
                    label="Confirm password"
                    icon={Lock}
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                  />

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full inline-flex items-center justify-center gap-3 px-5 py-3.5 rounded-xl text-white font-bold text-sm transition-colors shadow-lg outline-none focus-visible:ring-4 min-w-0 hover:brightness-95 disabled:opacity-70"
                    style={{ background: PRIMARY, boxShadow: `0 14px 30px -10px ${PRIMARY}55` }}
                  >
                    {loading && <Loader2 size={16} className="animate-spin shrink-0" aria-hidden="true" />}
                    <span>{loading ? 'Saving...' : 'Save and sign in'}</span>
                  </button>

                  <div className="text-center mt-4">
                    <button
                      type="button"
                      onClick={() => setStep('signin')}
                      className="text-sm font-bold transition-colors hover:underline"
                      style={{ color: PRIMARY }}
                    >
                      Back to Sign In
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <>
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#0d0d0d] text-center mt-1 uppercase">
                  SIGN IN
                </h2>
                <p className="mt-1.5 text-[11px] sm:text-xs uppercase text-slate-500 text-center whitespace-nowrap overflow-hidden text-ellipsis px-2">
                  organization admin and team members access login.
                </p>

                <form className="mt-4 space-y-3" onSubmit={submit}>
                  <OrgPicker
                    orgs={activeOrgs}
                    selectedId={orgId}
                    onSelect={setOrgId}
                  />

                  {/* One box, and what it asks for is this shop's own setting -
                      see `method` above. A shop on mobile numbers never sees the
                      word Email, and nobody at a counter has to choose. */}
                  <FormInput
                    label={idLabel}
                    icon={IdIcon}
                    type={idType}
                    placeholder={idPlaceholder}
                    value={identity}
                    onChange={(e) => setIdentity(cleanIdentity(e.target.value))}
                    autoComplete={idAutoComplete}
                    required
                  />

                  <div className="relative">
                    <FormInput
                      label="Password"
                      icon={Lock}
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      rightIcon={showPassword ? EyeOff : Eye}
                      onRightIconClick={() => setShowPassword((v) => !v)}
                      autoComplete="current-password"
                      required
                    />
                    <div className="text-right mt-1.5">
                      <button
                        type="button"
                        onClick={() => sendCode()}
                        disabled={loading}
                        className="text-[11px] sm:text-xs font-bold hover:underline transition-colors disabled:opacity-60"
                        style={{ color: PRIMARY }}
                      >
                        Forgot Password?
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="w-full inline-flex items-center justify-center gap-3 px-5 py-3.5 rounded-xl text-white font-bold text-sm transition-colors shadow-lg outline-none focus-visible:ring-4 min-w-0 hover:brightness-95"
                    style={{
                      background: selectedOrg?.theme.primary || '#0a5132',
                      boxShadow: `0 14px 30px -10px ${selectedOrg?.theme.primary || '#0a5132'}55`,
                    }}
                  >
                    <span>SIGN IN</span>
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </motion.main>
      </div>
    </div>
  );
};

OrgLogin.propTypes = {
  setView: PropTypes.func,
  onLogin: PropTypes.func.isRequired,
  showNotification: PropTypes.func,
  isLoaded: PropTypes.bool,
  applyTheme: PropTypes.func,
  hideBack: PropTypes.bool,
};